import { useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import emailjs from "@emailjs/browser";
import validator from "validator";
import { ImSpinner3 } from "react-icons/im";
import { FaEnvelope, FaExclamationCircle } from "react-icons/fa";

// ---------- STYLED COMPONENTS ----------

const ContactSection = styled.section`
  background: #f9f9f9;
  padding: 80px 20px;
  text-align: center;
`;

const ContactContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: #3d6c4d;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SectionSubtitle = styled.p`
    font-size: 18px;
    color: #666;
    margin-bottom: 30px;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #3d6c4d;
`;

const Input = styled.input`
    padding: 14px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 16px;
    transition: 0.2s;

    &:focus {
        border-color: #c58e50;
        outline: none;
        box-shadow: 0 0 4px rgba(197, 142, 80, 0.4);
    }
`;

const TextArea = styled.textarea`
    padding: 14px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 16px;
    resize: none;
    min-height: 100px;

    &:focus {
        border-color: #c58e50;
        outline: none;
        box-shadow: 0 0 4px rgba(197, 142, 80, 0.4);
    }
`;

const SubmitButton = styled.button`
    background: #c58e50;
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    border: none;
    padding: 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: 0.3s;

    &:hover {
        background: #a8743c;
    }
`;

const breatheAnimation = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const SpinningIcon = styled(ImSpinner3)`
    font-size: 40px;
    color: #35644c;
    animation: ${breatheAnimation} 1.5s linear infinite;
    margin-bottom: 16px;
`;

const SuccessIcon = styled(FaEnvelope)`
    font-size: 40px;
    color: #3d6c4d;
    margin-bottom: 16px;
`;

const ErrorIcon = styled(FaExclamationCircle)`
    font-size: 40px;
    color: #c0392b;
    margin-bottom: 16px;
`;

const Message = styled.p`
    font-size: 18px;
    color: #3d6c4d;
`;

const ErrorMessage = styled.p`
    font-size: 18px;
    color: #c0392b;
`;

// ---------- COMPONENT ----------

function Contact() {
    const formToSend = useRef();
    const [formState, setFormState] = useState("initial");
    const [emailError, setEmailError] = useState(null);
    const [nameError, setNameError] = useState(null);
    const [phoneError, setPhoneError] = useState(null);

    const validateEmail = () => {
        let email = formToSend.current.email.value;
        if (email && validator.isEmail(email)) {
            setEmailError(null);
            return true;
        } else {
            setEmailError("Моля, въведете валиден имейл.");
            return false;
        }
    };

    const validateName = () => {
        if (formToSend.current.name.value) {
            setNameError(null);
            return true;
        } else {
            setNameError("Полето не може да бъде празно.");
            return false;
        }
    };

    const validatePhone = () => {
        if (formToSend.current.phone.value) {
            setPhoneError(null);
            return true;
        } else {
            setPhoneError("Моля, въведете валиден телефон.");
            return false;
        }
    };

    const sendEmail = (e) => {
        e.preventDefault();

        let isEmailValid = validateEmail();
        let isNameValid = validateName();
        let isPhoneValid = validatePhone();

        if (isEmailValid && isNameValid && isPhoneValid) {
            setFormState("sending");
            emailjs.sendForm('service_b2aonui', 'template_pc3yts3', formToSend.current, '0dVNNPAf748H1jySQ')
                .then(() => {
                    setFormState("success");
                })
                .catch(() => {
                    setFormState("error");
                });
        }
    };

    return (
        <ContactSection>
            <ContactContainer>
                {formState === "initial" && (
                    <>
                        <SectionTitle>Свържете се с нас</SectionTitle>
                        <SectionSubtitle>
                            Оставете ни съобщение и ще се свържем с вас.
                        </SectionSubtitle>
                        <Form ref={formToSend}>
                            <Label htmlFor="email">Имейл *</Label>
                            <Input type="email" name="email" id="email" />
                            {emailError && <ErrorMessage>{emailError}</ErrorMessage>}

                            <Label htmlFor="name">Име *</Label>
                            <Input type="text" name="name" id="name" />
                            {nameError && <ErrorMessage>{nameError}</ErrorMessage>}

                            <Label htmlFor="phone">Телефон *</Label>
                            <Input type="text" name="phone" id="phone" />
                            {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}

                            <Label htmlFor="message">Съобщение</Label>
                            <TextArea name="message" id="message" />

                            <SubmitButton type="button" onClick={(e) => sendEmail(e)}>
                                Изпрати
                            </SubmitButton>
                        </Form>
                    </>
                )}

                {formState === "sending" && (
                    <>
                        <SpinningIcon />
                        <Message>Изпращане на съобщението...</Message>
                    </>
                )}

                {formState === "success" && (
                    <>
                        <SuccessIcon />
                        <Message>Вашето съобщение беше изпратено успешно!</Message>
                    </>
                )}

                {formState === "error" && (
                    <>
                        <ErrorIcon />
                        <ErrorMessage>Възникна проблем. Опитайте отново.</ErrorMessage>
                    </>
                )}
            </ContactContainer>
        </ContactSection>
    );
}

export default Contact;
